import { ConvertArgs, UnitConverter } from './types';

export abstract class MetricConverter<Unit extends string, BaseUnit extends Unit> extends UnitConverter<
    Unit,
    BaseUnit
> {
    protected multiplerRegex: RegExp | null = null;

    toBase({ value, from }: Parameters<UnitConverter<Unit, BaseUnit>['toBase']>[0]) {
        return {
            value: value * this.getMultiplier(from),
            unit: this.baseUnit,
        };
    }

    convert<ToUnit extends Unit>({ value, from, to }: ConvertArgs<Unit, ToUnit>) {
        const { value: baseValue } = this.toBase({ value, from });

        return {
            value: baseValue / this.getMultiplier(to),
            unit: to,
        };
    }

    protected getUnitParts(unit: string, baseUnit: string = this.baseUnit) {
        const match = unit.match(new RegExp(`([a-zA-Z]?)${baseUnit}(\\d*)`));
        return {
            prefix: match?.[1] || '',
            suffix: match?.[2] || '',
        };
    }

    protected getMultiplier(unit: string) {
        let multiplier = 1;

        const { prefix, suffix } = this.getUnitParts(unit);

        multiplier = getBaseMultiplierFromPrefix(prefix) ?? multiplier;

        if (suffix === '2') {
            multiplier ^= 2;
        } else if (suffix === '3') {
            multiplier ^= 3;
        }

        return multiplier;
    }
}

const IMPERIAL_TO_METER: Record<string, number> = {
    ft: 0.3048, // 1 foot = 0.3048 meters
    in: 0.0254, // 1 inch = 0.0254 meters
} as const;

export abstract class PerUnitLengthMetricConverter<
    TUnit extends string,
    TBaseUnit extends TUnit,
> extends MetricConverter<TUnit, TBaseUnit> {
    private getLengthMultiplier(lengthUnit: string): number {
        if (lengthUnit in IMPERIAL_TO_METER) {
            return IMPERIAL_TO_METER[lengthUnit];
        }

        const { prefix } = this.getUnitParts(lengthUnit, 'm');
        const metricPrefix = getBaseMultiplierFromPrefix(prefix);

        return metricPrefix ?? 1;
    }

    getMultiplier(unit: TUnit): number {
        const [quantityUnit, lengthUnit = ''] = unit.split('/');
        const [baseQuantityUnit] = this.baseUnit.split('/');

        const { prefix: quantityPrefix } = this.getUnitParts(quantityUnit, baseQuantityUnit);
        const quantityMultiplier = getBaseMultiplierFromPrefix(quantityPrefix) || 1;

        const lengthMultiplier = this.getLengthMultiplier(lengthUnit);

        return quantityMultiplier / lengthMultiplier;
    }
}

const getBaseMultiplierFromPrefix = (prefix?: string) => {
    switch (prefix) {
        case 'm':
            return 1e-3;
        case 'c':
            return 1e-2;
        case 'k':
            return 1e3;
        case 'M':
            return 1e6;
        case 'G':
            return 1e9;
        case 'T':
            return 1e12;
        case 'u':
            return 1e-6;
        case 'n':
            return 1e-9;
        case 'p':
            return 1e-12;
    }
};
